import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ContentService } from 'app/store/services/content.service';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { HttpErrorResponse } from '@angular/common/http';
import { LeftMenuService } from 'app/store/services/left-menu.service';
import { SessionService } from '../../store/services/session.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserType } from '../../store/models/user.model';

@Component({
  selector: 'app-edit-quick-response',
  templateUrl: './edit-quick-response.component.html',
  styleUrls: ['./edit-quick-response.component.scss'],
})
export class EditQuickResponseComponent implements OnInit, OnDestroy {
  state: 'edit' | 'create';
  responseId: number;
  formGroup: FormGroup;
  ready: boolean;

  error: { name?: string; response?: string };

  cancelButtonProperties: Button = {
    size: ButtonSize.SMALL,
    color: ButtonColor.BLUE,
    text: 'Cancel',
    onClick: () => this.router.navigate(['/contents/quick_responses']),
  };

  saveButtonProperties: Button = {
    size: ButtonSize.SMALL,
    color: ButtonColor.ORANGE,
    onClick: () => this.saveOrUpdateResponse(),
    text: null,
  };

  private readonly destroy$ = new Subject<boolean>();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private contentService: ContentService,
    private leftMenuService: LeftMenuService,
    private readonly sessionService: SessionService,
  ) {
  }

  get response() {
    return this.formGroup.get('response').value;
  }

  ngOnInit() {
    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe(userSession => {
        if (
          userSession &&
          [UserType.PARTNER, UserType.MANAGER].indexOf(
            userSession.user_type,
          ) !== -1
        ) {
          this.router.navigate(['/']);
          return;
        }

        this.leftMenuService.expand('content');
        this.state = this.activatedRoute.snapshot.data.state;

        if (this.state === 'edit') {
          this.responseId = this.activatedRoute.snapshot.params.id;
          this.contentService
            .getQuickResponse(this.responseId)
            .subscribe(responseData => {
              this.initializeForm(responseData.name, responseData.response);
            });
        } else {
          this.initializeForm();
        }
      });
  }

  getSaveButtonText() {
    return this.state === 'create' ? 'Create' : 'Save';
  }

  saveOrUpdateResponse() {
    if (this.formGroup.valid) {
      if (this.state === 'create') {
        this.contentService
          .createQuickResponse(this.formGroup.get('name').value, this.response)
          .subscribe(
            () => this.router.navigate(['contents', 'quick_responses']),
            error => this.formatError(error),
          );
      } else {
        this.contentService
          .updateQuickResponse(
            this.responseId,
            this.formGroup.get('name').value,
            this.response,
          )
          .subscribe(
            () => this.router.navigate(['contents', 'quick_responses']),
            error => this.formatError(error),
          );
      }
    } else {
      this.formGroup.markAllAsTouched();
    }
  }

  formatError(error: HttpErrorResponse) {
    if (error && error.error) {
      let eMessage = null;
      try {
        eMessage = JSON.parse(error.error.message);
      } catch (e) {
        console.log('Could not parse error object:', error.error.message);
      }

      if (eMessage) {
        Object.keys(eMessage).forEach(key => {
          if (!this.error) {
            this.error = {
              [key]: null,
            };
          }

          this.error[key] = eMessage[key][0];
        });
      }
    }
  }

  initializeForm(name = '', response = '') {
    this.formGroup = new FormGroup({
      name: new FormControl(name, [Validators.required]),
      response: new FormControl(response, [Validators.required]),
    });

    this.ready = true;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
